import * as dagre from "@dagrejs/dagre";

import { getCanonicalBlockType, isConditionalFlowRef } from "../workflowManifest";

const graphlib = dagre.graphlib;
const Graph = graphlib.Graph;

function isInputReference(value) {
    return typeof value === "string" && value.startsWith("$inputs");
}

function isStepReference(value) {
    return typeof value === "string" && value.startsWith("$steps");
}

function isConditionalForwardReference(step, propertyName, value) {
    const canonicalBlockType = getCanonicalBlockType(step.type);

    const isRef = isStepReference(value);
    const isConditionalStep = canonicalBlockType === "Condition";
    const isforwardRef = propertyName == "steps_if_true" || propertyName == "steps_if_false";
    const isConditional = isConditionalFlowRef(canonicalBlockType);
    const isContinueIfNextStepsRef = propertyName == "next_steps";
    return (
        (isRef && isConditionalStep && isforwardRef) ||
        (isRef && isConditional && isContinueIfNextStepsRef)
    );
}

export function parseWorkflowToGraph(workflowSpec) {
    const inputs = workflowSpec?.inputs;
    const steps = workflowSpec?.steps || [];
    const outputs = workflowSpec?.outputs || [];

    const g = new Graph();

    // add input node
    g.setNode("$inputs", { type: "Input", inputs: inputs });

    // add nodes and edges for all the steps
    steps.forEach((step) => {
        const stepId = `$steps.${step.name}`;
        g.setNode(stepId, step);

        // Check for dependencies and add edges of all steps
        Object.keys(step).forEach((key) => {
            // key is the property name of the step
            // value is the property value
            let values = step[key];

            if (!Array.isArray(values)) {
                if (values instanceof Object) {
                    // dictionary must be unpacked into list of values, as we may find selectors there
                    values = Object.values(values);
                } else {
                    values = [values];
                }
            }

            for (let val of values) {
                if (isInputReference(val)) {
                    g.setEdge("$inputs", stepId);
                }

                // edges to other steps
                if (isStepReference(val)) {
                    const stepMatch = val.match(/(\$steps\.\w+)/);
                    if (stepMatch) {
                        // conditionals are the only blocks that hold refs to blocks coming after them
                        // rather than referencing properties to pull from
                        if (isConditionalForwardReference(step, key, val)) {
                            g.setEdge(stepId, stepMatch[1]);
                        } else {
                            g.setEdge(stepMatch[1], stepId);
                        }
                    }
                }
            }
        });
    });

    // add output node and edges from steps pointing to it
    g.setNode("$outputs", { type: "Output", outputs: outputs });
    outputs.forEach((output) => {
        const outputId = `$outputs`;

        // Check for dependencies and add edges of all ouptuts
        Object.keys(output).forEach((key) => {
            const value = output[key];
            if (isStepReference(value)) {
                const stepMatch = value.match(/(\$steps\.\w+)/);
                if (stepMatch) {
                    g.setEdge(stepMatch[1], outputId);
                }
            }
        });
    });

    if (!steps.length) {
        // Add a new node with type "GetStarted" that connects to the inputs and outputs
        g.setNode("$getStarted", { type: "GetStarted" });
        g.setEdge("$inputs", "$getStarted");
        g.setEdge("$getStarted", "$outputs");
    }

    return g;
}

/*
transitive reduction of a directed graph D is another directed graph with the same vertices and as few edges as possible
see e.g. https://en.wikipedia.org/wiki/Transitive_reduction

this implementation is based mostly on the the description here:  https://brunoscheufler.com/blog/2021-12-05-decreasing-graph-complexity-with-transitive-reductions
but changed to use the graphlib library instead of a custom graph implementation
*/
export function transitiveReduction(g) {
    if (!graphlib.alg.isAcyclic(g)) {
        throw new Error("Graph has cycles");
    }
    // Start without any edges, this is important!
    const transitiveReduction = new Graph();
    const descendants: Map<string, Set<string>> = new Map<string, Set<string>>();
    const checkCount: Map<string, number> = new Map<string, number>();

    // init nodes in the transitive reduction graph
    // and checkCount with number of incoming edges for each node
    for (const n of g.nodes()) {
        transitiveReduction.setNode(n, g.node(n));
        checkCount.set(n, g.inEdges(n).length);
    }

    // console.log(checkCount);

    // Go over all nodes in the graph
    for (const n of g.nodes()) {
        // Find successor nodes of n
        const successors = new Set(g.successors(n));

        // Go over all neighbouring nodes (reading from graph again since we'll modify "neighbors")
        for (const m of g.successors(n)) {
            if (successors.has(m)) {
                if (!descendants.has(m)) {
                    // Find descendants of m with post order traversal
                    // (but dont include the node itself)
                    const reachableNodes = graphlib.alg.preorder(g, m).filter((x) => x != m);
                    // console.log("descendants of m", n, m, nodeDescendants);
                    descendants.set(m, new Set(reachableNodes)); // Convert Set to array
                }
                // Delete all descendants of m from neighbors
                for (const d of Array.from(descendants.get(m))) {
                    successors.delete(d);
                }
            }
            checkCount.set(m, checkCount.get(m)! - 1);
            if (checkCount.get(m) === 0) {
                descendants.delete(m);
            }
        }

        // Add edges to our transitive reduction again
        for (const o of Array.from(successors)) {
            transitiveReduction.setEdge(n, o);
        }
    }

    return transitiveReduction;
}
